/**
 * The account's settings sections, in display order. Groups and section names
 * match the location's (locationSections.ts) wherever they mean the same
 * thing — Business › Profile, People; Operations › Work orders, Time &
 * attendance; Money › Pricing, Accounting; Connections › Feeds, Partners — so
 * a location's list reads as the account's, less what only an account has. The money surfaces that
 * used to be Financials sub-tabs are sections of their own (Invoicing,
 * Pricing, Accounting, Your TURN-CONNECT bill), so nothing sits three levels
 * deep. Every gate is the rule the old builders used (see access.ts).
 */
import { invoicingSectionIds, INVOICING_SECTION_LABELS, type InvoicingSectionId } from '../../modals/invoicingSections';
import {
  canOpenAccountFeeds,
  canOpenAccounting,
  canOpenBill,
  canOpenCustomers,
  canOpenPartners,
  canOpenPdr,
  canOpenReporting,
  canSeeAccountPolicySurfaces,
  canWriteAccountDefaults,
  canWriteAccountPricing,
  has,
} from './access';
import type { SettingsAccess, SettingsSectionDef, SettingsTabDef } from './types';

const edit = (a: SettingsAccess) => a.isEditMode;
const editAnd = (rule: (a: SettingsAccess) => boolean) => (a: SettingsAccess) => a.isEditMode && rule(a);

const legacyPolicyWrite = (a: SettingsAccess) => a.isSystemAdmin || has(a, 'policies:manage') || has(a, 'accounts:update');
const canWriteWorkOrderRules = (a: SettingsAccess) => legacyPolicyWrite(a) || has(a, 'policies:workorders:manage');
const canWriteEditLock = (a: SettingsAccess) => legacyPolicyWrite(a) || has(a, 'policies:accounting:manage');
const canWriteTimeAttendance = (a: SettingsAccess) => legacyPolicyWrite(a) || has(a, 'policies:worksessions:manage');
const canManagePdr = (a: SettingsAccess) => a.isSystemAdmin || has(a, 'pdr:manage');
const directoryListingEnabled = () => import.meta.env.REACT_APP_ENABLE_DIRECTORY_LISTING === 'true';

const invoicingTab = (id: InvoicingSectionId): SettingsTabDef => ({
  id,
  label: INVOICING_SECTION_LABELS[id],
  visible: (a) => invoicingSectionIds([...a.permissions]).includes(id),
});

export const ACCOUNT_SECTIONS: readonly SettingsSectionDef[] = [
  { id: 'overview', label: 'Overview', group: '', scope: 'account', save: 'none', visible: edit, keywords: ['setup', 'to-do', 'summary'] },
  {
    id: 'customizations',
    label: 'Location customizations',
    group: '',
    scope: 'account',
    save: 'instant',
    visible: editAnd(canSeeAccountPolicySurfaces),
    keywords: ['deviations', 'overrides', 'differ', 'location values'],
  },
  {
    id: 'details',
    label: 'Profile',
    group: 'Business',
    scope: 'account',
    save: 'explicit',
    keywords: ['name', 'logo', 'contact', 'timezone', 'tax id', 'website', 'service splits', 'split'],
  },
  {
    id: 'locations',
    // The same words as the location's own Profile › group: locations and the groups they sit in.
    label: 'Locations & groups',
    group: 'Business',
    scope: 'account',
    save: 'instant',
    visible: edit,
    tabs: [
      { id: 'list', label: 'Locations' },
      { id: 'groups', label: 'Groups', keywords: ['location groups'] },
    ],
  },
  {
    id: 'people',
    label: 'People',
    group: 'Business',
    scope: 'account',
    save: 'mixed',
    visible: edit,
    tabs: [
      { id: 'users', label: 'Users', keywords: ['staff', 'employees', 'disable user'] },
      { id: 'invitations', label: 'Invitations', keywords: ['invite', 'pending invitations'] },
      { id: 'roles', label: 'Roles', keywords: ['permissions', 'access'] },
    ],
  },
  {
    id: 'work-orders',
    label: 'Work orders',
    group: 'Operations',
    scope: 'account',
    save: 'mixed',
    visible: editAnd(canSeeAccountPolicySurfaces),
    tabs: [
      {
        id: 'rules',
        label: 'Rules',
        locked: (a) => !canWriteWorkOrderRules(a),
        keywords: ['required fields', 'share link', 'work order rules'],
      },
      {
        id: 'checks',
        label: 'Completion checks & PIN',
        locked: (a) => !canWriteWorkOrderRules(a),
        keywords: ['pin', 'photos', 'duplicate vin'],
      },
      { id: 'edit-lock', label: 'Lock after sync', locked: (a) => !canWriteEditLock(a), keywords: ['edit lock', 'synced'] },
    ],
  },
  {
    // The optional Customers module's switch (and where Customer required lives).
    id: 'customers',
    label: 'Customers',
    group: 'Operations',
    scope: 'account',
    save: 'instant',
    visible: editAnd(canOpenCustomers),
    keywords: ['customer list', 'walk-in', 'dealer', 'fleet', 'customer required'],
  },
  {
    id: 'time-attendance',
    label: 'Time & attendance',
    group: 'Operations',
    scope: 'account',
    save: 'mixed',
    visible: editAnd(canSeeAccountPolicySurfaces),
    tabs: [
      { id: 'geofence', label: 'Clock-in area', locked: (a) => !canWriteTimeAttendance(a), keywords: ['geofence', 'radius'] },
      { id: 'safeguards', label: 'Timesheet safeguards', locked: (a) => !canWriteTimeAttendance(a), keywords: ['fraud', 'auto logout'] },
      { id: 'breaks', label: 'Breaks', locked: (a) => !canWriteTimeAttendance(a) },
      { id: 'approvals', label: 'Pay periods & approvals', locked: (a) => !canWriteTimeAttendance(a), keywords: ['pay period', 'payroll'] },
      { id: 'payrules', label: 'Pay rules', locked: (a) => !canWriteTimeAttendance(a), keywords: ['overtime', 'rounding'] },
    ],
  },
  {
    id: 'invoicing',
    label: 'Invoicing',
    group: 'Money',
    scope: 'account',
    save: 'instant',
    // Only for someone with at least one of its tabs.
    visible: editAnd((a) => invoicingSectionIds([...a.permissions]).length > 0),
    tabs: (
      [
        'coverage',
        'schedules',
        'deliveryLists',
        'options',
        'contacts',
        'whoGetsWhat',
        'history',
        'sentEmails',
        'accounting',
        'aiCheckup',
      ] as const
    ).map(invoicingTab),
    keywords: ['invoice', 'auto-send', 'batch', 'coverage', 'gaps'],
  },
  {
    id: 'pricing',
    label: 'Pricing',
    group: 'Money',
    scope: 'account',
    save: 'mixed',
    visible: edit,
    tabs: [
      {
        id: 'defaults',
        label: 'Labor rate & auto-approval',
        locked: (a) => !canWriteAccountDefaults(a),
        keywords: ['labor rate', 'auto-approval'],
      },
      {
        id: 'discounts',
        label: 'Discounts & fees',
        locked: (a) => !canWriteAccountPricing(a),
        keywords: ['discount', 'fee', 'pricing rules'],
      },
    ],
  },
  {
    id: 'accounting',
    label: 'Accounting',
    group: 'Money',
    scope: 'account',
    save: 'explicit',
    visible: editAnd(canOpenAccounting),
    tabs: [
      { id: 'accounting', label: 'Accounting', keywords: ['quickbooks', 'netsuite', 'books'] },
      { id: 'activityLog', label: 'Sync history' },
    ],
  },
  {
    id: 'pdr',
    label: 'PDR',
    group: 'Money',
    scope: 'account',
    save: 'instant',
    visible: editAnd(canOpenPdr),
    tabs: [
      { id: 'overview', label: 'Overview', visible: canManagePdr },
      { id: 'charts', label: 'Price charts' },
      { id: 'who-pays', label: 'Who pays' },
      { id: 'randi', label: 'R&I prices' },
      { id: 'rules', label: 'Estimate rules', visible: canManagePdr },
    ],
    keywords: ['paintless dent repair', 'dent'],
  },
  {
    id: 'bill',
    label: 'Your TURN-CONNECT bill',
    group: 'Money',
    scope: 'account',
    save: 'explicit',
    visible: editAnd(canOpenBill),
    keywords: ['billing', 'plan', 'subscription'],
  },
  {
    id: 'feeds',
    label: 'Feeds',
    group: 'Connections',
    scope: 'account',
    save: 'instant',
    visible: editAnd(canOpenAccountFeeds),
    keywords: ['inventory', 'vehicle feed'],
  },
  {
    id: 'partners',
    label: 'Partners',
    group: 'Connections',
    scope: 'account',
    save: 'instant',
    visible: editAnd(canOpenPartners),
    tabs: [
      { id: 'connections', label: 'Connections' },
      { id: 'mapping', label: 'Service matching' },
      { id: 'directory', label: 'Directory listing', visible: directoryListingEnabled },
    ],
  },
  {
    id: 'reporting',
    label: 'Reporting',
    group: 'Connections',
    scope: 'account',
    save: 'instant',
    visible: editAnd(canOpenReporting),
    keywords: ['scheduled reports', 'email'],
  },
];
